import { useEnvironmentVariables } from "#app/EnvironmentProvider.tsx";
import { ApiError, getJson, httpPut, postJson, resolveApiUrl } from "#lib/http/index.ts";
import type {
    CancelConversationFileUploadCommand,
    CompleteConversationFileUploadCommand,
    CompleteConversationFileUploadCommandResult,
    CreateConversationCommandResult,
    CreateConversationMessageCommand,
    DeleteConversationCommand,
    DeleteConversationFileCommand,
    Endpoints,
    GenerateConversationFileUploadUrlCommand,
    GenerateConversationFileUploadUrlCommandResult,
    GetChannelsDetailsQueryResult,
    GetConversationFilesQueryResult,
    GetConversationQueryResult,
    GetConversationsQueryResult,
    GetCurrentUserQueryResult,
    GetMembersByEmailsQuery,
    GetMembersByEmailsQueryResult,
    GetMembersByIdsQueryResult,
    GetMemberPrimaryTeamQueryResult,
    GetQuotaUsageQueryResult,
    GetSkillsQueryResult,
    GetSmartReportQueryResult,
    GetSpeechTokenQueryResult,
    GetPersonalizationQueryResult,
    SearchMembersQuery,
    SearchMembersQueryResult,
    UpdateConversationMessageRatingCommand,
    UpdateConversationMessageRatingExplanationCommand,
    UpdatePersonalizationCommand,
    UpdateSmartReportCommand
} from "#types/types.ts";
import { fetchEventSource, type FetchEventSourceInit } from "@microsoft/fetch-event-source";
import { useMemo } from "react";

export class AiApiClient {
    constructor(private readonly aiApiBaseUrl: string) { }

    private getEndpoint(endpoint: Endpoints) {
        return resolveApiUrl(endpoint, this.aiApiBaseUrl);
    }

    getCurrentUser = async () => {
        const response = await getJson(this.getEndpoint("/getCurrentUser"), { timeoutInMs: 10_000 });

        return response.data as GetCurrentUserQueryResult;
    };

    getConversations = async () => {
        const response = await getJson(this.getEndpoint("/getConversations"), { timeoutInMs: 30_000 });
        const data = response.data as GetConversationsQueryResult;

        return data.conversations;
    };

    getSmartReport = async () => {
        const response = await getJson(this.getEndpoint("/getSmartReport"), { timeoutInMs: 30_000 });

        return response.data as GetSmartReportQueryResult;
    };

    updateSmartReport = async (payload: UpdateSmartReportCommand) => {
        await postJson(this.getEndpoint("/updateSmartReport"), payload);
    };

    getConversation = async (conversationId: string) => {
        const response = await getJson(`${this.getEndpoint("/getConversation")}?conversationId=${conversationId}`);

        const data = response.data as GetConversationQueryResult;

        return data.conversation;
    };

    createConversation = async () => {
        const response = await postJson(this.getEndpoint("/createConversation"));

        const data = response.data as CreateConversationCommandResult;

        return data;
    };

    deleteConversation = async (payload: DeleteConversationCommand) => {
        await postJson(this.getEndpoint("/deleteConversation"), payload);
    };

    createConversationMessage = async (command: CreateConversationMessageCommand, options: FetchEventSourceInit) => {
        await fetchEventSource(this.getEndpoint("/createConversationMessage"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(command),
            ...options,
            openWhenHidden: true,
            credentials: "include"
        });
    };

    searchMembers = async (payload: SearchMembersQuery, signal?: AbortSignal) => {
        const response = await postJson(this.getEndpoint("/searchMembers"), payload, { signal });

        const data = response.data as SearchMembersQueryResult;

        return data.members;
    };

    getMembersByIds = async (memberIds: string[]) => {
        const query = memberIds.map(id => `memberIds=${encodeURIComponent(id)}`).join("&");
        const response = await getJson(`${this.getEndpoint("/getMembersByIds")}?${query}`, { timeoutInMs: 10_000 });
        const data = response.data as GetMembersByIdsQueryResult;

        return data.members;
    };

    getMembersByEmails = async (emails: string[]) => {
        const payload: GetMembersByEmailsQuery = { emails };
        const response = await postJson(this.getEndpoint("/getMembersByEmails"), payload, { timeoutInMs: 10_000 });
        const data = response.data as GetMembersByEmailsQueryResult;

        return data.members;
    };

    generateConversationFileUploadUrl = async (payload: GenerateConversationFileUploadUrlCommand, abortController: AbortController) => {
        const response = await postJson(this.getEndpoint("/generateConversationFileUploadUrl"), payload, { signal: abortController.signal });

        return response.data as GenerateConversationFileUploadUrlCommandResult;
    };

    completeConversationFileUpload = async (payload: CompleteConversationFileUploadCommand) => {
        const response = await postJson(this.getEndpoint("/completeConversationFileUpload"), payload, { timeoutInMs: 60_000 });

        return response.data as CompleteConversationFileUploadCommandResult;
    };

    deleteConversationFile = async (payload: DeleteConversationFileCommand) => {
        await postJson(this.getEndpoint("/deleteConversationFile"), payload, { timeoutInMs: 60_000 });
    };

    getConversationFiles = async (fileIds: string[]) => {
        const query = fileIds.map(id => `fileIds=${encodeURIComponent(id)}`).join("&");
        const response = await getJson(`${this.getEndpoint("/getConversationFiles")}?${query}`, { timeoutInMs: 10_000 });
        const data = response.data as GetConversationFilesQueryResult;

        return data.files;
    };

    cancelConversationFileUpload = async (payload: CancelConversationFileUploadCommand) => {
        await postJson(this.getEndpoint("/cancelConversationFileUpload"), payload, { timeoutInMs: 60_000 });
    };

    uploadFile = async (file: File, fileUploadUrl: string, abortController: AbortController) => {
        const response = await httpPut(fileUploadUrl, file, {
            headers: {
                "x-ms-blob-type": "BlockBlob",
                "Content-Type": file.type
            },
            signal: abortController.signal
        });

        if (!response.ok) {
            throw new ApiError(response);
        }
    };

    updateConversationMessageRating = async (payload: UpdateConversationMessageRatingCommand) => {
        await postJson(this.getEndpoint("/updateConversationMessageRating"), payload);
    };

    updateConversationMessageRatingExplanation = async (payload: UpdateConversationMessageRatingExplanationCommand) => {
        await postJson(this.getEndpoint("/updateConversationMessageRatingExplanation"), payload);
    };

    getChannelsDetails = async () => {
        const response = await getJson(this.getEndpoint("/getChannelsDetails"), { timeoutInMs: 10_000 });

        return response.data as GetChannelsDetailsQueryResult;
    };

    getPersonalization = async () => {
        const response = await getJson(this.getEndpoint("/getPersonalization"), { timeoutInMs: 10_000 });

        return response.data as GetPersonalizationQueryResult;
    };

    updatePersonalization = async (payload: UpdatePersonalizationCommand) => {
        await postJson(this.getEndpoint("/updatePersonalization"), payload);
    };

    getQuotaUsage = async () => {
        const response = await getJson(this.getEndpoint("/getQuotaUsage"), { timeoutInMs: 10_000 });

        return response.data as GetQuotaUsageQueryResult;
    };

    getMemberPrimaryTeam = async () => {
        const response = await getJson(this.getEndpoint("/getMemberPrimaryTeam"), { timeoutInMs: 10_000 });

        return response.data as GetMemberPrimaryTeamQueryResult;
    };

    getSpeechToken = async (signal?: AbortSignal) => {
        const response = await getJson(this.getEndpoint("/getSpeechToken"), { timeoutInMs: 10_000, signal });

        return response.data as GetSpeechTokenQueryResult;
    };

    getSkills = async () => {
        const response = await getJson(this.getEndpoint("/getSkills"), { timeoutInMs: 30_000 });
        const data = response.data as GetSkillsQueryResult;

        return data.skills;
    };
}

export function useAiApiClient() {
    const { aiApiBaseUrl } = useEnvironmentVariables();

    return useMemo(() => {
        return createAiApiClient(aiApiBaseUrl);
    }, [aiApiBaseUrl]);
}

export function createAiApiClient(aiApiBaseUrl?: string) {
    if (!aiApiBaseUrl) {
        throw new Error("[shared] ai api url is not defined");
    }

    return new AiApiClient(aiApiBaseUrl);
}
